import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useFormik } from "formik";
import * as Yup from "yup";

import { LocalImage } from "Utils/images";
import { Colors } from "Utils/colors";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import { TextField, Button, TextareaAutosize } from "@mui/material";
import { SnackBarContext } from "Context/SnackContext";
import moment from "moment";
import { InFlow } from "Features/services/inFlow.api";
import { CustomField } from "Components/Form/CustomField";
import { CustomRectangleButton } from "Components/Form";
import { Shipstation } from "Features/services/shipstation.api";
import CancelIcon from "@mui/icons-material/Cancel";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export interface UnitPrice {
  unitPrice: string;
}

export interface ProductObject {
  productId?: string;
  categoryId?: string;
  description?: string;
  isActive?: boolean;
  itemType?: string;
  lastModifiedDateTime?: string;
  name: string;
  sku: string;
  unitPrice: any;
  defaultPrice?: any;
  standardUomName?: string;
  totalQuantityOnHand: string;
  weight?: number;
  width?: number;
  defaultImage?: any;
  wholesalePrice?: string;
  company_cost?: string;
  company_class?: string;
  wg_purchase_price?: string;
  productWeight?: any;
  productLength?: any;
  productWidth?: any;
  productHeight?: any;
  packagingType?: string;
  unitPerPackage?: any;
  additionalShippingNote?: string;
  shipping_methods?: any;
  preferred_carrier?: any;
  quantity?: any;
  selectedCarriers?: any;
  selectedMethod?: any;
}

interface ShippingMethod {
  value: string;
  label: string;
  disabled?: boolean;
}

const shippingMethods: ShippingMethod[] = [
  { value: "freight", label: "Freight Only" },
  { value: "ground", label: "Ground", disabled: true },
  { value: "expedited", label: "Expedited", disabled: true },
  { value: "overnight", label: "Overnight", disabled: true },
];

interface IProps {
  closeModal: () => void;
  product: ProductObject;
  setProductUpdated: any;
}

const editProductSchema = Yup.object().shape({
  name: Yup.string().required("Product Name is required"),
  unitPrice: Yup.number().required("Unit Price is required"),
  sku: Yup.string().required("SKU is required"),
  totalQuantityAvailable: Yup.number(),
  totalQuantityOnHand: Yup.string().required(
    "Total Quantity On Hand is required"
  ),
  // itemType: Yup.string().required("Product Type is required"),
  description: Yup.string(),
  isActive: Yup.boolean(),
  lastModifiedDateTime: Yup.string(),
  wholesalePrice: Yup.string().required("Wholesale Price is required"),

  wg_purchase_price: Yup.string().test(
    "wgPurchasePriceRequired",
    "WG Purchase Price is required",
    (value: any, context: any) => {
      if (context.parent.company_class === "wgPurchasedProductPTP") {
        return value !== undefined;
      }
      return true;
    }
  ),

  // productWeight: Yup.number()
  //   .required("Product Weight is required")
  //   .min(1, "Product Weight must be greater than 0"),
  // productLength: Yup.number()
  //   .required("Product Length is required")
  //   .min(1, "Product Length must be greater than 0"),
  // productWidth: Yup.number()
  //   .required("Product Width is required")
  //   .min(1, "Product Width must be greater than 0"),
  // productHeight: Yup.number()
  //   .required("Product Height is required")
  //   .min(1, "Product Height must be greater than 0"),

  packagingType: Yup.string().required("Packaging Type is required"),
  unitPerPackage: Yup.number()
    .required("Unit Per Package is required")
    .min(1, "Unit Per Package must be greater than 0"),

  additionalShippingNote: Yup.string(),

  // preferred_carrier: Yup.array()
  // .of(Yup.string().oneOf(['fedex', 'stamps.com', 'ups']))
  // .min(1, "At least one preferred carrier is required")
  // .required("Preferred Carrier is required"),
});

export const EditProductInventory = ({
  closeModal,
  product,
  setProductUpdated,
}: IProps) => {
  const { handleSnackbar } = useContext(SnackBarContext);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [editMode, setEditMode] = useState(true);

  const [selectedCarriers, setSelectedCarriers] = useState<any>([]);
  const [shippingOptions, setShippingOptions] = useState<any>([]);
  const [carrierLoading, setCarrierLoading] = useState(false);

  const [selectedMethod, setSelectedMethod] = useState<string[]>([]);

  const [openCarrier, setOpenCarrier] = useState(false);
  const [openMethod, setOpenMethod] = useState(false);

  console.log("product--", product);

  const parseOrConvertToArray = (value: any) => {
    try {
      // If the value is a JSON string, parse it
      return typeof value === 'string' ? JSON.parse(value) : value;
    } catch (error) {
      // If parsing fails, convert the string to an array
      return Array.isArray(value) ? value : [value];
    }
  };

  const initialValues: ProductObject = {
    productId: product.productId || "",
    categoryId: product.categoryId || "",
    description: product.description || "",
    isActive: product.isActive !== undefined ? !product.isActive : true,
    name: product.name || "",
    sku: product.sku || "",
    unitPrice: product?.unitPrice || 0,
    wholesalePrice: product.wholesalePrice || "",
    wg_purchase_price: product.wg_purchase_price || "",
    company_cost: product.company_cost || "",
    company_class: product.company_class || "",
    standardUomName: product.standardUomName || "",
    totalQuantityOnHand: product.quantity || "",
    width: product.width || 0,

    weight: product.weight || 0,
    productWeight: product.productWeight || 0,
    productLength: product.productLength || 0,
    productWidth: product.productWidth || 0,
    productHeight: product.productHeight || 0,
    packagingType: product.packagingType || "",
    unitPerPackage: product.unitPerPackage || 0,
    additionalShippingNote: product.additionalShippingNote || "",

    // shipping_methods: product.selectedMethod || [],
    // preferred_carrier: product.selectedCarriers || [],

    shipping_methods: parseOrConvertToArray(product.shipping_methods),
    preferred_carrier: parseOrConvertToArray(product.preferred_carrier),
  };

  // useEffect(() => {
  //   if (product.preferred_carrier) {
  //     const carriers = JSON.parse(product.preferred_carrier);
  //     setSelectedCarriers(carriers);
  //   }

  //   if (product.shipping_methods) {
  //     const methods = JSON.parse(product.shipping_methods);
  //     setSelectedMethod(methods);
  //   }
  // }, [product]);

  useEffect(() => {
    if (product.preferred_carrier) {
      const carriers = parseOrConvertToArray(product.preferred_carrier);
      setSelectedCarriers(carriers);
    }
  
    if (product.shipping_methods) {
      const methods = parseOrConvertToArray(product.shipping_methods);
      setSelectedMethod(methods);
    }
  }, [product]);
  
  // Keep the same parseOrConvertToArray function


  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  let payload: any;

  const [originalValues, setOriginalValues] =
    useState<ProductObject>(initialValues);

  useEffect(() => {
    setOriginalValues(initialValues);
  }, []);

  const formik = useFormik({
    initialValues,
    validateOnChange: true,

    onSubmit: async (values) => {
      payload = {
        productId: values.productId,
        name: values.name,
        description: values.description,
        sku: values.sku,
        unitPrice: values.unitPrice,
        wholesalePrice: values.wholesalePrice,
        company_cost: values.company_cost,
        isActive: !values.isActive,
        wg_purchase_price: values.wg_purchase_price,
        company_class: values.company_class,
        quantity: values.totalQuantityOnHand,
        productWeight: values.productWeight,
        productLenght: values.productLength,
        productWidth: values.productWidth,
        productHeight: values.productHeight,
        additionalShippingNote: values.additionalShippingNote,
        packagingType: values.packagingType,
        unitPerPackage: values.unitPerPackage,
        shipping_methods: selectedMethod,
        preferred_carrier: selectedCarriers,
      };

      handleSubmit();
    },
  });

  const getUpdatedFields = () => {
    const updatedFields: Partial<ProductObject> = {};
    for (const key in formik.values) {
      if (
        key !== "productId" &&
        formik.values[key as keyof ProductObject] !==
          originalValues[key as keyof ProductObject]
      ) {
        updatedFields[key as keyof ProductObject] =
          formik.values[key as keyof ProductObject];
      }
    }
    return updatedFields;
  };

  const handleSubmit = async () => {
    if (formik.isValid) {
      const updatedFields = getUpdatedFields();

      if (Object.keys(updatedFields).length === 0) {
        handleSnackbar(true, "No changes detected", "info");
        closeModal();
        return;
      }
      setErrorMsg("");
      setIsLoading(true);
      updatedFields.productId = originalValues.productId;
      try {
        debugger;
        await InFlow.instance.editProduct(payload);
        handleSnackbar(true, "Successfully Edited", "success");
        setProductUpdated(true);
      } catch (error: any) {
        setErrorMsg(error?.response?.data?.message);
        handleSnackbar(true, error?.response?.data?.message, "error");
      } finally {
        setIsLoading(false);
        closeModal();
      }
    } else {
      handleSnackbar(true, "Please fix the missing fields", "error");
      closeModal();
    }
  };

  const handleResetForm = () => {
    formik.resetForm({
      values: {
        name: "",
        description: "",
        sku: "",
        unitPrice: "",
        totalQuantityOnHand: "",
      },
    });
  };

  useEffect(() => {
    const fetchListCarriers = async () => {
      setCarrierLoading(true);
      try {
        const response = await Shipstation.instance.listCarriers();
        console.log("Carrier Data:", response.data);
        setShippingOptions(response.data);
      } catch (error) {
        handleSnackbar(true, "Something went wrong. Try again", "error");
      } finally {
        setCarrierLoading(false);
      }
    };

    fetchListCarriers();
  }, []);

  useEffect(() => {
    console.log("Selected carriers---", selectedCarriers);
  }, [selectedCarriers]);

  const handleMethodChange = (event: any) => {
    const { value } = event.target;
    setSelectedMethod(
      typeof value === "string" ? value.split(",") : (value as string[])
    );
    setOpenMethod(false);

  };

  console.log("selecedMethod---", selectedMethod);

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Box
        sx={{ position: "relative", left: "40%", display: "flex", p: 0, m: 0 }}
      >
        {/* <FormGroup>
                    <FormControlLabel label="Edit sd"  control={<Switch defaultChecked color="warning" />} />
                </FormGroup> */}
      </Box>

      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid
          item
          sm={12}
          md={12}
          lg={12}
          sx={{
            height: "85vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "50%",
                    marginBottom: "-16px",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "75px",
                    height: "75px",
                  }}
                >
                  <img
                    src={
                      product?.defaultImage?.originalUrl ||
                      LocalImage.companyLogo
                    }
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    alt="Product"
                  />
                </Box>

                <Box
                  sx={{
                    borderRadius: "50%",
                    border: `1px solid #FFAA2A`,
                    padding: "4px",
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    top: "80px",
                    left: "85px",
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: "16px", color: "#FFAA2A" }} />
                </Box>

                <Box sx={{ marginLeft: "12px" }}>
                  <Typography
                    variant="body1"
                    component={"body"}
                    fontSize={"12px"}
                  >
                    Edit Product Inventory
                  </Typography>

                  <Typography
                    variant="h4"
                    fontWeight={"bold"}
                    component={"h4"}
                    color={Colors.primary}
                    fontSize={"22px"}
                  >
                    {product.name}
                  </Typography>
                </Box>
              </Box>

              <FormControlLabel
                label="Edit Mode"
                labelPlacement="start"
                control={
                  <Switch
                    checked={editMode}
                    onChange={() => setEditMode(!editMode)}
                    color="warning"
                  />
                }
              />
            </Box>

            <Box sx={{ border: `2px solid ${Colors.primary}` }} />
            <Box sx={{ border: `6px solid #A3CEDC` }} />

            <Grid container>
              <Grid sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                    >
                      MRSP:
                    </Typography>
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                    >
                      SKU:
                    </Typography>
                  </CardContent>
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                    >
                      $90.00/50
                    </Typography>
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                    >
                      {product.sku}
                    </Typography>
                  </CardContent>
                </Box>
              </Grid>

              <Grid sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                      fontWeight={"bold"}
                    >
                      Current Stock:
                    </Typography>
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                    >
                      Last Restock:
                    </Typography>
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                    >
                      Last Update:
                    </Typography>
                  </CardContent>
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                      fontWeight={"bold"}
                    >
                      {product.totalQuantityOnHand
                        ? parseFloat(product.totalQuantityOnHand).toFixed(2)
                        : "-"}
                    </Typography>
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                    >
                      Nov. 23 , 2023
                    </Typography>
                    <Typography
                      component={"body"}
                      variant="body1"
                      fontSize={"12px"}
                    >
                      {moment(product.lastModifiedDateTime).format(
                        "MMM D, YYYY h:mm A"
                      )}
                    </Typography>
                  </CardContent>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ width: "100%" }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                >
                  <Tab
                    label="Billing Information"
                    {...a11yProps(0)}
                    sx={{
                      fontSize: "12px",
                      textTransform: "capitalize",
                      color: Colors.primary,
                    }}
                  />
                  <Tab
                    label="Shipping Information"
                    {...a11yProps(1)}
                    sx={{
                      fontSize: "12px",
                      textTransform: "capitalize",
                      color: Colors.primary,
                    }}
                  />
                </Tabs>
              </Box>

              <form onSubmit={formik.handleSubmit}>
                <div
                  role="tabpanel"
                  hidden={value !== 0}
                  id={`simple-tabpanel-0`}
                  aria-labelledby={`simple-tab-0`}
                >
                  <Box sx={{ textAlign: "right", p: 0, m: 0 }}>
                    <FormControlLabel
                      label="Disable product"
                      control={
                        <Checkbox
                          id="isActive"
                          name="isActive"
                          checked={formik.values.isActive}
                          onChange={formik.handleChange}
                          disabled={!editMode}
                        />
                      }
                    />
                  </Box>

                  <Box sx={{ p: 0, m: 0, mt: "-20px" }}>
                    <CustomField
                      value={formik.values.name}
                      onChangeText={formik.handleChange("name")}
                      errorMessage={
                        formik.touched.name && formik.errors.name
                          ? formik.errors.name
                          : ""
                      }
                      isError={
                        formik.touched.name && Boolean(formik.errors.name)
                      }
                      handleBlur={formik.handleBlur("name")}
                      required={true}
                      label="Product Name*"
                      placeholder=""
                      size="small"
                      labelStyle={{ fontWeight: 400, color: "#666666" }}
                      disabled={!editMode}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: !editMode ? "none" : undefined,
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "-4px",
                      color: "red",
                      gap: 1,
                    }}
                  >
                    <CustomField
                      type="number"
                      value={formik.values.unitPrice}
                      onChangeText={formik.handleChange("unitPrice")}
                      errorMessage={
                        formik.touched.unitPrice && formik.errors.unitPrice
                          ? formik.errors.unitPrice
                          : ""
                      }
                      isError={
                        formik.touched.unitPrice &&
                        Boolean(formik.errors.unitPrice)
                      }
                      handleBlur={formik.handleBlur("unitPrice")}
                      required={true}
                      label="Unit Price*"
                      placeholder=""
                      size="small"
                      labelStyle={{ fontWeight: 400, color: "#666666" }}
                      containerStyle={{ flex: 0.8, marginTop: 1 }}
                      disabled={!editMode}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: !editMode ? "none" : undefined,
                        },
                      }}
                    />

                    <CustomField
                      value={formik.values.wholesalePrice}
                      onChangeText={formik.handleChange("wholesalePrice")}
                      errorMessage={
                        formik.touched.wholesalePrice &&
                        formik.errors.wholesalePrice
                          ? formik.errors.wholesalePrice
                          : ""
                      }
                      isError={
                        formik.touched.wholesalePrice &&
                        Boolean(formik.errors.wholesalePrice)
                      }
                      handleBlur={formik.handleBlur("wholesalePrice")}
                      required={true}
                      label="Wholesale Price*"
                      placeholder="Wholesale Price"
                      size="small"
                      labelStyle={{ fontWeight: 400, color: "#666666" }}
                      containerStyle={{ flex: 0.8, marginTop: 1 }}
                      disabled={!editMode}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: !editMode ? "none" : undefined,
                        },
                      }}
                    />

                    <CustomField
                      value={formik.values.company_cost}
                      onChangeText={formik.handleChange("company_cost")}
                      errorMessage={
                        formik.touched.company_cost &&
                        formik.errors.company_cost
                          ? formik.errors.company_cost
                          : ""
                      }
                      isError={
                        formik.touched.company_cost &&
                        Boolean(formik.errors.company_cost)
                      }
                      handleBlur={formik.handleBlur("company_cost")}
                      required={true}
                      label="Internal Pricing*"
                      placeholder="Company Cost"
                      size="small"
                      labelStyle={{ fontWeight: 400, color: "#666666" }}
                      containerStyle={{ flex: 0.8, marginTop: 1 }}
                      disabled={!editMode}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: !editMode ? "none" : undefined,
                        },
                      }}
                    />

                    <CustomField
                      value={formik.values.totalQuantityOnHand}
                      onChangeText={formik.handleChange("totalQuantityOnHand")}
                      errorMessage={
                        formik.touched.totalQuantityOnHand &&
                        formik.errors.totalQuantityOnHand
                          ? formik.errors.totalQuantityOnHand
                          : ""
                      }
                      isError={
                        formik.touched.totalQuantityOnHand &&
                        Boolean(formik.errors.totalQuantityOnHand)
                      }
                      handleBlur={formik.handleBlur("totalQuantityOnHand")}
                      required={true}
                      label="Total Quantity On Hand*"
                      placeholder=""
                      size="small"
                      labelStyle={{ fontWeight: 400, color: "#666666" }}
                      disabled={!editMode}
                      containerStyle={{ flex: 0.8, marginTop: 1 }}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: !editMode ? "none" : undefined,
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "-6px",
                      gap: 1,
                    }}
                  >
                    <CustomField
                      value={formik.values.sku}
                      onChangeText={formik.handleChange("sku")}
                      errorMessage={
                        formik.touched.sku && formik.errors.sku
                          ? formik.errors.sku
                          : ""
                      }
                      isError={formik.touched.sku && Boolean(formik.errors.sku)}
                      handleBlur={formik.handleBlur("sku")}
                      required={true}
                      label="SKU*"
                      size="small"
                      labelStyle={{ fontWeight: 400, color: "#666666" }}
                      disabled={!editMode}
                      containerStyle={{ flex: 1 }}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: !editMode ? "none" : undefined,
                        },
                      }}
                    />

                    <Box sx={{ flex: 1, mt: 1.5 }}>
                      <InputLabel
                        sx={{
                          fontSize: 12,
                          mb: "2px",
                          fontWeight: 400,
                          color: "#666666",
                        }}
                      >
                        Company Class
                      </InputLabel>

                      <Select
                        labelId="company_class"
                        id="company_class"
                        name="company_class"
                        value={formik.values.company_class}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.company_class &&
                          Boolean(formik.errors.company_class)
                        }
                        size="small"
                        sx={{
                          fontSize: 13,
                          mt: 0.5,
                          pb: 0.2,
                          height: "32px",
                          width: "100%",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: !editMode ? "none" : undefined,
                          },
                        }}
                        disabled={!editMode}
                      >
                        <MenuItem value="productforPTP">
                          Product for PTP
                        </MenuItem>
                        <MenuItem value="wgPurchasedProductPTP">
                          WG Purchased Product for PTP
                        </MenuItem>
                        <MenuItem value="barrierCableProduct">
                          Barrier Cable Product
                        </MenuItem>
                        <MenuItem value="wgProduct">WG Product</MenuItem>
                      </Select>
                    </Box>

                    {formik.values.company_class ===
                      "wgPurchasedProductPTP" && (
                      <CustomField
                        value={formik.values.wg_purchase_price}
                        onChangeText={formik.handleChange("wg_purchase_price")}
                        errorMessage={
                          formik.touched.wg_purchase_price &&
                          formik.errors.wg_purchase_price
                            ? formik.errors.wg_purchase_price
                            : ""
                        }
                        isError={
                          formik.touched.wg_purchase_price &&
                          Boolean(formik.errors.wg_purchase_price)
                        }
                        handleBlur={formik.handleBlur("wg_purchase_price")}
                        required={
                          formik.values.company_class ===
                          "wgPurchasedProductPTP"
                        }
                        label="WG Purchase Price*"
                        size="small"
                        labelStyle={{ fontWeight: 400, color: "#666666" }}
                        containerStyle={{ flex: 1 }}
                        disabled={!editMode}
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: !editMode ? "none" : undefined,
                          },
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <InputLabel
                      sx={{
                        fontSize: 12,
                        mb: 0.5,
                        fontWeight: 400,
                        color: "#666666",
                      }}
                    >
                      Product Description*
                    </InputLabel>

                    <TextareaAutosize
                      aria-label="minimum height"
                      minRows={3}
                      value={formik.values.description}
                      name="description"
                      onChange={formik.handleChange}
                      style={{
                        width: "100%",
                        border: !editMode ? "none" : "1px solid #ccc",
                        resize: "none",
                        borderRadius: "8px",
                        padding: 2,
                      }}
                      className="font-poppins"
                      disabled={!editMode}
                    />
                  </Box>
                </div>
              </form>

              <div
                role="tabpanel"
                hidden={value !== 1}
                id={`simple-tabpanel-1`}
                aria-labelledby={`simple-tab-1`}
                style={{ marginTop: "12px" }}
              >
                <Typography sx={{ fontSize: "15px", fontWeight: "semibold" }}>
                  Shipping Dimensions
                </Typography>
                <form>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexDirection: "row",
                          flexWrap: "wrap",
                          width: "100%",
                        }}
                      >
                        <Grid container spacing={1} sx={{ flex: 1 }}>
                          <Grid item xs={12} sm={3}>
                            <CustomField
                              type="number"
                              value={formik.values.productWeight}
                              onChangeText={formik.handleChange(
                                "productWeight"
                              )}
                              errorMessage={
                                formik.touched.productWeight &&
                                formik.errors.productWeight
                                  ? formik.errors.productWeight
                                  : ""
                              }
                              isError={
                                formik.touched.productWeight &&
                                Boolean(formik.errors.productWeight)
                              }
                              handleBlur={formik.handleBlur("productWeight")}
                              required={true}
                              label="Product Weight (Ibs)*"
                              size="small"
                              labelStyle={{ fontWeight: 400, color: "#666666" }}
                              disabled={!editMode}
                              containerStyle={{ flex: 1 }}
                              sx={{
                                flex: 1,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: !editMode ? "none" : undefined,
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <CustomField
                              type="number"
                              value={formik.values.productLength}
                              onChangeText={formik.handleChange(
                                "productLength"
                              )}
                              errorMessage={
                                formik.touched.productLength &&
                                formik.errors.productLength
                                  ? formik.errors.productLength
                                  : ""
                              }
                              isError={
                                formik.touched.productLength &&
                                Boolean(formik.errors.productLength)
                              }
                              handleBlur={formik.handleBlur("productLength")}
                              required={true}
                              label="Length"
                              size="small"
                              labelStyle={{ fontWeight: 400, color: "#666666" }}
                              disabled={!editMode}
                              containerStyle={{ flex: 1 }}
                              sx={{
                                flex: 1,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: !editMode ? "none" : undefined,
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <CustomField
                              type="number"
                              value={formik.values.productWidth}
                              onChangeText={formik.handleChange("productWidth")}
                              errorMessage={
                                formik.touched.productWidth &&
                                formik.errors.productWidth
                                  ? formik.errors.productWidth
                                  : ""
                              }
                              isError={
                                formik.touched.productWidth &&
                                Boolean(formik.errors.productWidth)
                              }
                              handleBlur={formik.handleBlur("productWidth")}
                              required={true}
                              label="Width"
                              size="small"
                              labelStyle={{ fontWeight: 400, color: "#666666" }}
                              disabled={!editMode}
                              containerStyle={{ flex: 1 }}
                              sx={{
                                flex: 1,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: !editMode ? "none" : undefined,
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <CustomField
                              type="number"
                              value={formik.values.productHeight}
                              onChangeText={formik.handleChange(
                                "productHeight"
                              )}
                              errorMessage={
                                formik.touched.productHeight &&
                                formik.errors.productHeight
                                  ? formik.errors.productHeight
                                  : ""
                              }
                              isError={
                                formik.touched.productHeight &&
                                Boolean(formik.errors.productHeight)
                              }
                              handleBlur={formik.handleBlur("productHeight")}
                              required={true}
                              label="Height"
                              size="small"
                              labelStyle={{ fontWeight: 400, color: "#666666" }}
                              disabled={!editMode}
                              containerStyle={{ flex: 1 }}
                              sx={{
                                flex: 1,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: !editMode ? "none" : undefined,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      <div
                        role="tabpanel"
                        hidden={value !== 1}
                        id={`simple-tabpanel-1`}
                        aria-labelledby={`simple-tab-1`}
                        style={{ marginTop: "10px" }}
                      >
                        Package Information
                      </div>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexDirection: "row",
                          flexWrap: "wrap",
                          width: "100%",
                        }}
                      >
                        <Box sx={{ flex: 1, mt: 1 }}>
                          <InputLabel
                            sx={{
                              fontSize: 12,
                              mb: "2px",
                              fontWeight: 400,
                              color: "#666666",
                              marginTop: "5px",
                            }}
                          >
                            Packaging Type
                          </InputLabel>

                          <Select
                            labelId="packagingType"
                            id="packagingType"
                            name="packagingType"
                            value={formik.values.packagingType}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.packagingType &&
                              Boolean(formik.errors.packagingType)
                            }
                            size="small"
                            sx={{
                              fontSize: 13,
                              mt: 0.5,
                              pb: 0.2,
                              height: "32px",
                              width: "100%",
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: !editMode ? "none" : undefined,
                              },
                            }}
                            disabled={!editMode}
                          >
                            <MenuItem value="box">Box</MenuItem>
                            <MenuItem value="envelope">Envelope</MenuItem>
                            <MenuItem value="pallet">Pallet</MenuItem>
                            <MenuItem value="crate">Crate</MenuItem>
                          </Select>
                        </Box>

                        <CustomField
                          type="number"
                          value={formik.values.unitPerPackage}
                          onChangeText={formik.handleChange("unitPerPackage")}
                          errorMessage={
                            formik.touched.unitPerPackage &&
                            formik.errors.unitPerPackage
                              ? formik.errors.unitPerPackage
                              : ""
                          }
                          isError={
                            formik.touched.unitPerPackage &&
                            Boolean(formik.errors.unitPerPackage)
                          }
                          handleBlur={formik.handleBlur("unitPerPackage")}
                          required={true}
                          label="Unit per Package"
                          size="small"
                          labelStyle={{ fontWeight: 400, color: "#666666" }}
                          disabled={!editMode}
                          containerStyle={{ flex: 1 }}
                          sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: !editMode ? "none" : undefined,
                            },
                          }}
                        />
                      </Box>

                      <Grid
                        container
                        alignItems="center"
                        spacing={2}
                        sx={{ mt: -1 }}
                      >
                        <Grid item xs={6}>
                          <Box>
                            <InputLabel
                              sx={{
                                fontSize: 12,
                                mb: "2px",
                                fontWeight: 400,
                                color: "#666666",
                              }}
                            >
                              Available Shipping Methods
                            </InputLabel>

                            {/* <Select
                              labelId="shipping_methods"
                              id="shipping_methods"
                              name="shipping_methods"
                              value={formik.values.shipping_methods}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.shipping_methods &&
                                Boolean(formik.errors.shipping_methods)
                              }
                              size="small"
                              sx={{
                                fontSize: 13,
                                mt: 0.5,
                                pb: 0.2,
                                height: "32px",
                                width: "100%",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: !editMode ? "none" : undefined,
                                },
                              }}
                              disabled={!editMode}
                            >
                              <MenuItem value="freight">
                               Freight Only
                              </MenuItem>                              
                              <MenuItem value="ground">Ground</MenuItem>
                              <MenuItem value="expedited">Expedited</MenuItem>
                              <MenuItem value="overnight">Overnight</MenuItem>
                            </Select> */}

                            <Select
                              labelId="shipping_methods"
                              id="shipping_methods"
                              multiple
                              open={openMethod}
                              onOpen={() => setOpenMethod(true)}
                              onClose={() => setOpenMethod(false)}
                              value={selectedMethod}
                              onChange={handleMethodChange}
                              renderValue={(selected) => selected.join(", ")}
                              error={
                                formik.touched.shipping_methods &&
                                Boolean(formik.errors.shipping_methods)
                              }
                              sx={{
                                width: "100%",
                                minHeight: "32px",
                              }}
                            >
                              {shippingMethods.map((method) => (
                                <MenuItem
                                  key={method.value}
                                  value={method.value}
                                  disabled={method.disabled}
                                >
                                  <Checkbox
                                    checked={selectedMethod.includes(
                                      method.value
                                    )}
                                    disabled={method.disabled}
                                  />
                                  <ListItemText primary={method.label} />
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </Grid>

                        <Grid item xs={6}>
                          <Box>
                            <InputLabel
                              sx={{
                                fontSize: 12,
                                mb: "2px",
                                fontWeight: 400,
                                color: "#666666",
                              }}
                            >
                              Preferred Carrier
                            </InputLabel>

                            {/* <Select
                              labelId="preferred_carrier"
                              id="preferred_carrier"
                              name="preferred_carrier"
                              value={formik.values.preferred_carrier}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.preferred_carrier &&
                                Boolean(formik.errors.preferred_carrier)
                              }
                              size="small"
                              sx={{
                                fontSize: 13,
                                mt: 0.5,
                                pb: 0.2,
                                height: "32px",
                                width: "100%",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: !editMode ? "none" : undefined,
                                },
                              }}
                              disabled={!editMode}
                            >
                              {carrierLoading ? (
                                <MenuItem disabled>Loading...</MenuItem>
                              ) : (
                                shippingOptions.map((option: any, index: number) => (
                                  <MenuItem key={index} value={option.code}>
                                    {option.name}
                                  </MenuItem>
                                ))
                              )}

                            </Select> */}

                            <Select
                              labelId="preferred_carrier"
                              id="preferred_carrier"
                              multiple
                              open={openCarrier}
                              onOpen={() => setOpenCarrier(true)}
                              onClose={() => setOpenCarrier(false)}
                              value={selectedCarriers}
                              onChange={(e) => {
                                setSelectedCarriers(e.target.value as string[]);
                                setOpenCarrier(false); }
                              }
                            
                              renderValue={(selected) => (
                                <Stack gap={1} direction="row" flexWrap="wrap">
                                  {selected.map((value: any) => (
                                    <Chip
                                      key={value}
                                      label={value}
                                      onDelete={() =>
                                        setSelectedCarriers(
                                          selectedCarriers.filter(
                                            (item: any) => item !== value
                                          )
                                        )
                                      }
                                      deleteIcon={
                                        <CancelIcon
                                          onMouseDown={(event) =>
                                            event.stopPropagation()
                                          }
                                        />
                                      }
                                    />
                                  ))}
                                </Stack>
                              )}
                              sx={{
                                width: "100%",
                                minHeight: "32px",
                              }}
                            >
                              {shippingOptions.map(
                                (option: any, index: number) => (
                                  <MenuItem key={index} value={option.code}>
                                    {option.name}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </Box>
                        </Grid>
                      </Grid>
                    </div>

                    <div style={{ marginTop: "15px" }}>
                      <Box sx={{ mt: 0 }}>
                        <InputLabel
                          sx={{
                            fontSize: 12,
                            mb: 0.5,
                            fontWeight: 400,
                            color: "#666666",
                            marginTop: "-2px",
                          }}
                        >
                          Additional Shipping Note
                        </InputLabel>

                        <TextareaAutosize
                          aria-label="minimum height"
                          minRows={3}
                          value={formik.values.additionalShippingNote}
                          name="additionalShippingNote"
                          onChange={formik.handleChange}
                          style={{
                            width: "100%",
                            border: !editMode ? "none" : "1px solid #ccc",
                            resize: "none",
                            borderRadius: "5px",
                            padding: 2,
                            height: "200px",
                          }}
                          className="font-poppins"
                          disabled={!editMode}
                        />
                      </Box>
                    </div>
                  </div>
                </form>
              </div>

              <div
                role="tabpanel"
                hidden={value !== 2}
                id={`simple-tabpanel-2`}
                aria-labelledby={`simple-tab-2`}
              >
                Upsell Products
              </div>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <CustomRectangleButton
                  type={"button"}
                  label={"Clear"}
                  variant={"outlined"}
                  labelColor="#FFAA2A"
                  buttonContainerStyle={{
                    border: "1px solid #FFAA2A",
                    fontWeight: 700,
                  }}
                  onBtnClick={handleResetForm}
                  uppercase={false}
                  buttonnSize={"sm"}
                />

                <CustomRectangleButton
                  type={"button"}
                  label={"Update"}
                  variant={"outlined"}
                  labelColor="#fff"
                  buttonColor="#FFAA2A"
                  buttonContainerStyle={{
                    border: "1px solid #FFAA2A",
                    fontWeight: 700,
                    marginLeft: "20px",
                  }}
                  onBtnClick={() => formik.handleSubmit()}
                  uppercase={false}
                  buttonnSize={"sm"}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
